import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import type { KitAssetTrim } from 'fym-shared/src/music24/fullVideoPlan';
import { TypedMemo } from '../../../../common/types';
import { fullVideoMessages } from '../fullVideoMessages';
import { StudioDetailsProps } from './props';
import { DetailField } from './DetailField';
import { FullVideoLengthField } from './FullVideoLengthField';
import { FullVideoLyricsRow } from './FullVideoLyricsRow';
import type { SongLyricsView } from '../../../../smartLinks/catalog/song/page/catalogSongPath';

interface FullVideoDetailsProps extends StudioDetailsProps {
  onOpenSongLyrics: (view?: SongLyricsView) => void;
}

/**
 * Step 3 of a full video (design s7): the length (the whole song or a trim), the lyrics row of the
 * lyric video, and the YouTube end screen of the long videos with the shared end card (the last
 * 20 s keep the right half free). The music video has no step 3: its card opens the music video
 * editor (spec 2026-10-09 D11). The style cards and the Instructions come from `DetailsStep`.
 */
const FullVideoDetailsComponent: React.FC<FullVideoDetailsProps> = (props) => {
  const { themeId, details, song, onChange, onOpenSongLyrics } = props;
  const intl = useIntl();
  const changeTrim = useCallback((trim: KitAssetTrim | undefined) => onChange({ trim }), [onChange]);

  return (
    <>
      <FullVideoLengthField durationSec={song.durationSec} trim={details.trim} onChange={changeTrim} />
      {themeId === 'lyricVideo' && (
        <FullVideoLyricsRow captions={false} song={song} onOpenSongLyrics={onOpenSongLyrics} />
      )}
      {(themeId === 'lyricVideo' || themeId === 'visualizer') && (
        <DetailField title={intl.formatMessage(fullVideoMessages.endScreenTitle)}>
          <span className="small text-muted" data-testid="studio-end-screen-hint">
            {intl.formatMessage(fullVideoMessages.endScreenHint)}
          </span>
        </DetailField>
      )}
    </>
  );
};

export const FullVideoDetails = TypedMemo(FullVideoDetailsComponent);
